import { useCallback } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';
import { OWNERSHIP_TYPES } from '../../utils/stockOwnership';

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;
const OWNERSHIP_VALUES = new Set(OWNERSHIP_TYPES.map((type) => type.value));

/**
 * Texts of Stok Yönetimi (`stock.*`). Stored values stay Turkish ('sarf',
 * 'aktif', ownership keys...); these helpers only label them. Excel files and
 * stock type names (user data) are not translated.
 */
const useStockText = () => {
  const { t, language, locale } = useTranslation();
  const label = useCallback((key, vars) => t(`stock.${key}`, vars), [t]);
  const statusLabel = useCallback((value) => (value === 'aktif' || value === 'inaktif' ? t(`stock.status.${value}`) : value || '-'), [t]);
  const categoryLabel = useCallback((value) => {
    if (value === 'sarf' || value === 'demirbas') return t(`stock.categories.${value}`);
    return t('stock.categories.unknown');
  }, [t]);
  const ownershipLabel = useCallback((value) => (OWNERSHIP_VALUES.has(value) ? t(`stock.ownership.${value}`) : ''), [t]);
  /** 'YYYY-MM-DD' → short local date; other stored text is shown as it is. */
  const formatDay = useCallback((value) => {
    if (!value) return '';
    const match = String(value).match(ISO_DAY);
    if (!match) return String(value);
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString(locale);
  }, [locale]);
  /** ISO date-time of a movement → local date and time. */
  const formatDateTime = useCallback((value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }, [locale]);
  const formatNumber = useCallback((value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString(locale, { maximumFractionDigits: 2 }) : String(value ?? '');
  }, [locale]);
  return { t, language, locale, label, statusLabel, categoryLabel, ownershipLabel, formatDay, formatDateTime, formatNumber };
};

export default useStockText;
